import React, { useEffect, useState } from "react"
import Button from "../../../components/Button/Button"
import { Arrow } from "../../../components/Icons/Icons"
import { areFieldsNotEmpty } from "../../../helpers/helper"
import { calculatorValues } from "./calculatorValues"
import SearchAutoComplete from "../../../components/AutoComplete/SearchAutoComplete"
import { v4 as uuidv4 } from "uuid"
import Input from "../../../components/Input/Input"
import { editNewLeadDetails, getLeadDetails } from "../../../features/contacts/contacts"
import { useMyContextLoading } from "../../../components/LoadingContextProvider/LoadingContextProvider"
import { useDispatch } from "react-redux"
import { showSnackBar } from "../../../components/Snackbar/Snackbar"


export const BudgetCalculator = ({
  calculatorItems,
  setCalculatorItems,
  userId
}) => {

  const { triggerLoading } = useMyContextLoading()
  const dispatch = useDispatch()

  const [searchInput, setSearchInput] = useState("")
  const [selectedValue, setSelectedValue] = useState("")
  const [totalIncome, setTotalIncome] = useState(0)
  const [totalExpenses, setTotalExpenses] = useState(0)
  const [totalCash, setTotalCash] = useState(0)
  const [allAssets, setAllAssets] = useState(0)
  const [reqError, setReqError] = useState('')

  useEffect(() => {
    const incomeTotal = calculatorItems
      ?.filter(item => item.categoryType === "income" && item.category === "Monthly Income")
      ?.reduce((sum, item) => sum + (parseFloat(item.value) || 0), 0)
    setTotalIncome(incomeTotal)

    const cash = calculatorItems
      ?.filter(item => item.categoryType === "income" && item.category === "Liquid Assets")
      ?.reduce((sum, item) => sum + (parseFloat(item.value) || 0), 0)
    setTotalCash(cash)

    const assets = calculatorItems
      ?.filter(item => item.categoryType === "income" && item.category === "Liquid Assets" || item.category === "Other Assets")
      ?.reduce((sum, item) => sum + (parseFloat(item.value) || 0), 0)
    setAllAssets(assets)

    const expensesTotal = calculatorItems
      ?.filter(item => item.categoryType === "expenses")
      ?.reduce((sum, item) => sum + (parseFloat(item.value) || 0), 0)
    setTotalExpenses(expensesTotal)
  }, [calculatorItems])

  useEffect(() => {
    if (selectedValue) {
      const newItem = {
        id: uuidv4(),
        label: selectedValue.label,
        categoryType: selectedValue.type,
        category: selectedValue.category,
        value: "",
      }
      setCalculatorItems(prevItems => [...prevItems, newItem])
      setSelectedValue("")
      setSearchInput("")
    }
  }, [selectedValue])

  const handleInputChange = (id, e) => {
    let { value } = e.target

    if (/^\d*\.?\d{0,2}$/.test(value)) {
      if (value.startsWith('0') && value?.length > 1 && !value.startsWith('0.')) {
        value = value?.slice(1)
      }

      setCalculatorItems(prevItems =>
        prevItems?.map(item =>
          item.id === id ? { ...item, value } : item
        )
      )
    }
  }


  useEffect(() => {
    const handleReqError = () => {
      reqError.length > 0 && showSnackBar("error", reqError)
      setReqError('')
    }

    handleReqError()
  }, [reqError])

  useEffect(() => {
    userId && getData()
  }, [])

  const getData = async () => {
    triggerLoading(true)
    try {
      const response = await dispatch(getLeadDetails(userId))
      setCalculatorItems(response?.payload?.data?.budgetCalculator)
    } catch (error) {
      console.error(error)
      setReqError(error.message || 'Failed to get customer data')
    }
    triggerLoading(false)
  }


  const updateContact = async () => {
    triggerLoading(true)
    const postData = {
      budgetCalculator: calculatorItems?.map(item => {
        const newItem = { ...item, amount: Number(item.value) }
        delete newItem.id
        return newItem
      })
    }
    try {
      const response = await dispatch(editNewLeadDetails({ userId, data: postData }))
      if (response.error) {
        setReqError(response.payload)
      } else {
        showSnackBar("success", "Calculator successfully updated")
      }
    } catch (error) {
      setReqError('An unexpected error occurred. Please try again.')
    }
    triggerLoading(false)
  }


  const calculatePercentage = (A, B) => {
    if (A === 0) return 0
    const percentage = (B / A) * 100
    return Math.round(percentage)
  }

  const financialOverview = [
    {
      label: "Monthly Income",
      value: `$${totalIncome}`,
    },
    {
      label: "Monthly Expenses",
      value: `-$${totalExpenses}`,
    },
    {
      label: "Cash Flow",
      value: `${totalIncome - totalExpenses >= 0
        ? `$${totalIncome - totalExpenses}`
        : `-$${Math?.abs(totalIncome - totalExpenses)}`}`,
    },
    {
      label: "Debt-to-Income ",
      value: `${calculatePercentage(totalIncome, totalExpenses)}%`,
    },
    {
      label: "Total Cash ",
      value: `$${totalCash}`,
    },
    {
      label: "All Assets",
      value: `$${allAssets}`,
    },
  ]


  return (
    <>
      <div className="bg-surface border border-divider shadow-lg rounded-2xl w-full max-w-[798px]">
        <div className="font-bold text-xl-20 pt-[24px] pl-[48px] pb-[16px]">
          Financial Overview
        </div>
        <div className="border-b border-divider mx-6" />

        <div className="grid grid-cols-3 gap-6 m-6">
          {financialOverview?.map((item, index) => (
            <div key={index}>
              <div className="bg-surface border border-divider shadow-lg rounded-2xl p-6">
                <div className="text-[14px] font-semibold">
                  {item?.label}
                </div>
                <div className="text-[32px] font-bold pt-[8px]"
                  style={{
                    color: (() => {
                      return item?.label === "Monthly Expenses"
                        ? "var(--color-error-600)"
                        : item?.label === "Cash Flow"
                          ? (parseFloat(item?.value?.replace(/[^0-9.-]+/g, "")) >= 0 ? "var(--color-success-600)" : "var(--color-error-600)")
                          : ""
                    })()
                  }}>
                  {item?.value}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-surface border border-divider shadow-lg rounded-2xl mt-6 w-full max-w-[798px]">
        <div className="font-bold text-xl-20 pt-[24px] pl-[48px] pb-[16px]">
          Calculator
        </div>
        <div className="border-b border-divider mx-6" />
        <div className="mt-6 mx-12 mb-8">
          <SearchAutoComplete
            options={calculatorValues}
            setInputValue={setSearchInput}
            inputValue={searchInput}
            items={calculatorItems}
            setSelectedValue={setSelectedValue}
            selectedValue={selectedValue}
            placeholder="Search income & expenses..."
            type="search"
          />
        </div>
        <div className="flex mx-12 mb-12">
          <div className="pr-4 pt-4 border-r border-divider flex-1">
            <div className="flex gap-2">
              <div>
                <Arrow />
              </div>
              <div className="font-semibold text-[14px]">Income</div>
            </div>
            <div className="mt-6">
              {calculatorItems?.filter(item => item.categoryType === "income")
                ?.map((item) => (
                  <Input
                    parentClassName="my-6"
                    key={item.id}
                    value={item.value}
                    onChange={(e) => handleInputChange(item.id, e)}
                    placeholder="Enter value"
                    label={item.label}
                    leftSideIcon={<div className="text-text-secondary">$</div>}
                    leftSideIconStyle="ml-5 mt-[17px]"
                  />
                ))}
            </div>
          </div>
          <div className="pl-4 pt-4 flex-1 ">
            <div className="flex gap-2">
              <div>
                <Arrow fill="var(--color-error-600)" fillBg="var(--color-error-100)" isArrowUp={false} />
              </div>
              <div className="font-semibold text-[14px]">Expenses</div>
            </div>
            <div className="mt-6">
              {calculatorItems?.filter(item => item.categoryType === "expenses")
                ?.map((item) => (
                  <Input
                    key={item.id}
                    value={item.value}
                    onChange={(e) => handleInputChange(item.id, e)}
                    placeholder="Enter value"
                    label={item.label}
                    leftSideIcon={<div className="text-text-secondary">$</div>}
                    leftSideIconStyle="ml-5 mt-[17px]"
                    parentClassName="my-6"
                  />
                ))}
            </div>
          </div>
        </div>
      </div>

      {
        userId &&
        <div className="mt-6 mb-6 flex justify-end space-x-4">
          <Button
            disabled={!areFieldsNotEmpty(calculatorItems?.map(item => item.value))}
            onClick={updateContact}
          >
            Save
          </Button>
        </div>
      }
    </>
  )
}